import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  getDay,
} from 'date-fns';
import {
  Plane,
  Car,
  Hotel,
  Package,
  Filter,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  List,
  DollarSign,
  FileText,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Card, CardContent, CardHeader } from '@/app/components/ui/card';import { Badge } from '@/app/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  useTravelRecords,
  useTechProfiles,
  useAssignments,
} from '@/hooks/use-crew';
import type { TravelRecord, TravelType } from '@/lib/types';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';

const travelTypeLabels: Record<TravelType, string> = {
  flight: 'Flight',
  car_rental: 'Car Rental',
  hotel: 'Hotel',
  other: 'Other',
};

const travelTypeIcons: Record<TravelType, React.ElementType> = {
  flight: Plane,
  car_rental: Car,
  hotel: Hotel,
  other: Package,
};

const travelTypeColors: Record<TravelType, string> = {
  flight: 'bg-blue-100 text-blue-800',
  car_rental: 'bg-green-100 text-green-800',
  hotel: 'bg-purple-100 text-purple-800',
  other: 'bg-gray-100 text-gray-800',
};

export function TravelPage() {
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [techFilter, setTechFilter] = useState<string>('all');
  const [assignmentFilter, setAssignmentFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const { data: techsData } = useTechProfiles({ page_size: 200, status: 'active' });
  const { data: assignmentsData } = useAssignments({ page_size: 200 });

  const filters: Record<string, unknown> = { page_size: 200 };
  if (techFilter !== 'all') filters.tech = Number(techFilter);
  if (assignmentFilter !== 'all') filters.assignment = Number(assignmentFilter);
  if (typeFilter !== 'all') filters.travel_type = typeFilter;
  if (dateFrom) filters.date_from = dateFrom;
  if (dateTo) filters.date_to = dateTo;

  const { data: travelData, isLoading } = useTravelRecords(
    filters as Parameters<typeof useTravelRecords>[0]
  );

  const techs = techsData?.results ?? [];
  const assignments = assignmentsData?.results ?? [];
  const records = travelData?.results ?? [];

  const sortedRecords = useMemo(
    () => [...records].sort((a, b) => a.departure_date.localeCompare(b.departure_date)),
    [records]
  );

  const totalCost = useMemo(
    () => records.reduce((sum, r) => sum + (r.cost ? parseFloat(r.cost) : 0), 0),
    [records]
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Plane className="h-7 w-7 text-blue-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Travel</h1>
          </div>
          <p className="text-gray-600 mt-1">Manage travel records and logistics</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Records</p>
                <p className="text-2xl font-bold text-blue-600">{records.length}</p>
              </div>
              <List className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Flights</p>
                <p className="text-2xl font-bold text-blue-600">
                  {records.filter((r) => r.travel_type === 'flight').length}
                </p>
              </div>
              <Plane className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Hotels</p>
                <p className="text-2xl font-bold text-purple-600">
                  {records.filter((r) => r.travel_type === 'hotel').length}
                </p>
              </div>
              <Hotel className="h-8 w-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Cost</p>
                <p className="text-2xl font-bold text-green-600">
                  ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <Select value={techFilter} onValueChange={setTechFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Technicians" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Technicians</SelectItem>
                {techs.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Assignments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignments</SelectItem>
                {assignments.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.project_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="flight">Flight</SelectItem>
                <SelectItem value="car_rental">Car Rental</SelectItem>
                <SelectItem value="hotel">Hotel</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              placeholder="From date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input
              type="date"
              placeholder="To date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {records.length} record{records.length !== 1 ? 's' : ''}
        </p>
        <Tabs value={view} onValueChange={(v) => setView(v as 'list' | 'calendar')}>
          <TabsList>
            <TabsTrigger value="list">
              <List className="h-4 w-4 mr-1" />
              List
            </TabsTrigger>
            <TabsTrigger value="calendar">
              <CalendarDays className="h-4 w-4 mr-1" />
              Calendar
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : view === 'list' ? (
        <TravelList records={sortedRecords} />
      ) : (
        <TravelCalendar
          records={records}
          month={calendarMonth}
          onPrev={() => setCalendarMonth(subMonths(calendarMonth, 1))}
          onNext={() => setCalendarMonth(addMonths(calendarMonth, 1))}
          onToday={() => setCalendarMonth(new Date())}
        />
      )}
    </div>
  );
}

function TravelList({ records }: { records: TravelRecord[] }) {
  const timeZone = useOrgTimeZone();
  if (records.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Plane className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No travel records found</p>
          <p className="text-sm text-gray-400 mt-1">Adjust filters or add travel to an assignment</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {records.map((record) => {
        const Icon = travelTypeIcons[record.travel_type];
        return (
          <Card key={record.id}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <Icon className="h-5 w-5 text-gray-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{record.description}</p>
                      <Badge className={travelTypeColors[record.travel_type]}>
                        {travelTypeLabels[record.travel_type]}
                      </Badge>
                    </div>
                    {record.tech_name && (
                      <p className="text-sm text-gray-600 mt-0.5">
                        <Link
                          to={`/crew/techs/${record.tech}`}
                          className="text-blue-600 hover:underline"
                        >
                          {record.tech_name}
                        </Link>
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      {formatDateOnly(record.departure_date)}
                      {record.return_date && ` - ${formatDateOnly(record.return_date)}`}
                    </p>
                    {record.confirmation_number && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Conf: {record.confirmation_number}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {record.cost && (
                    <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                      ${parseFloat(record.cost).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  )}
                  {record.assignment && (
                    <Link
                      to={`/crew/travel/itinerary?assignment=${record.assignment}`}
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1 whitespace-nowrap"
                    >
                      <FileText className="h-3 w-3" />
                      Itinerary
                    </Link>
                  )}
                </div>
              </div>

              {/* Type-specific details */}
              {record.flight_info && (
                <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                  <p className="font-medium">
                    {record.flight_info.airline} {record.flight_info.flight_number}
                  </p>
                  <p className="text-gray-600">
                    {record.flight_info.departure_airport} &rarr; {record.flight_info.arrival_airport}
                  </p>
                  {record.flight_info.departure_time && (
                    <p className="text-xs text-gray-500">
                      Departs: {formatDateTimeInTimeZone(record.flight_info.departure_time, timeZone)}
                      {record.flight_info.arrival_time &&
                        ` | Arrives: ${formatDateTimeInTimeZone(record.flight_info.arrival_time, timeZone)}`}
                    </p>
                  )}
                </div>
              )}
              {record.car_rental && (
                <div className="mt-3 p-2 bg-green-50 rounded text-sm">
                  <p className="font-medium">
                    {record.car_rental.rental_company}
                    {record.car_rental.vehicle_type && ` - ${record.car_rental.vehicle_type}`}
                  </p>
                  <p className="text-gray-600">
                    {record.car_rental.pickup_location} &rarr; {record.car_rental.dropoff_location}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDateTimeInTimeZone(record.car_rental.pickup_date, timeZone)} -{' '}
                    {formatDateTimeInTimeZone(record.car_rental.dropoff_date, timeZone)}
                  </p>
                </div>
              )}
              {record.hotel_booking && (
                <div className="mt-3 p-2 bg-purple-50 rounded text-sm">
                  <p className="font-medium">{record.hotel_booking.hotel_name}</p>
                  {record.hotel_booking.address && (
                    <p className="text-gray-600">{record.hotel_booking.address}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    Check-in: {formatDateOnly(record.hotel_booking.check_in_date)} | Check-out:{' '}
                    {formatDateOnly(record.hotel_booking.check_out_date)}
                  </p>
                  {record.hotel_booking.confirmation_number && (
                    <p className="text-xs text-gray-400">
                      Conf: {record.hotel_booking.confirmation_number}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function TravelCalendar({
  records,
  month,
  onPrev,
  onNext,
  onToday,
}: {
  records: TravelRecord[];
  month: Date;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}) {
  const days = eachDayOfInterval({
    start: startOfMonth(month),
    end: endOfMonth(month),
  });

  const startPad = getDay(startOfMonth(month));
  const today = new Date();

  const recordsByDay = useMemo(() => {
    const map = new Map<string, TravelRecord[]>();
    records.forEach((r) => {
      const depDate = r.departure_date;
      const retDate = r.return_date ?? r.departure_date;
      const start = parseISO(depDate);
      const end = parseISO(retDate);
      if (start > end) return;
      const dayRange = eachDayOfInterval({ start, end });
      dayRange.forEach((d) => {
        const key = format(d, 'yyyy-MM-dd');
        const existing = map.get(key) ?? [];
        existing.push(r);
        map.set(key, existing);
      });
    });
    return map;
  }, [records]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onPrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-lg font-semibold sm:min-w-[180px] text-center">
              {format(month, 'MMMM yyyy')}
            </h2>
            <Button variant="outline" size="sm" onClick={onNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onToday} className="text-blue-600">
              Today
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="bg-gray-50 p-2 text-center text-xs font-medium text-gray-500">
              {d}
            </div>
          ))}
          {Array.from({ length: startPad }).map((_, i) => (
            <div key={`pad-${i}`} className="bg-white p-2 min-h-[80px]" />
          ))}
          {days.map((day) => {
            const key = format(day, 'yyyy-MM-dd');
            const dayRecords = recordsByDay.get(key) ?? [];
            const isToday = isSameDay(day, today);
            return (
              <div
                key={key}
                className={`bg-white p-2 min-h-[80px] ${
                  !isSameMonth(day, month) ? 'bg-gray-50' : ''
                }`}
              >
                <div
                  className={`text-xs font-medium mb-1 ${
                    isToday
                      ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center'
                      : 'text-gray-700'
                  }`}
                >
                  {format(day, 'd')}
                </div>
                <div className="space-y-0.5">
                  {dayRecords.slice(0, 3).map((r) => {
                    const Icon = travelTypeIcons[r.travel_type];
                    return (
                      <div
                        key={r.id}
                        className={`text-[10px] px-1 py-0.5 rounded truncate flex items-center gap-1 ${travelTypeColors[r.travel_type]}`}
                        title={`${r.tech_name}: ${r.description}`}
                      >
                        <Icon className="h-2.5 w-2.5 flex-shrink-0" />
                        <span className="truncate">{r.tech_name?.split(' ')[0]}</span>
                      </div>
                    );
                  })}
                  {dayRecords.length > 3 && (
                    <div className="text-[10px] text-gray-500 pl-1">
                      +{dayRecords.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
